import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, switchMap, throwError } from 'rxjs';
import {
  BillingEntry,
  CallNoteDetails,
  CallNoteDocumentDto,
  CallNoteEntry,
  CdrAccountingEntry,
  TranscriptEntry,
  TranscriptEntryDto,
} from './call-details.model';
import { of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { buildRecordingUrl, toAgentId } from './recording-url';

/**
 * The agent id rides along because the audit line (AD-13) needs `employee_id`,
 * and re-parsing it out of the URL would be a second site that knows the
 * template (AD-2 forbids that).
 *
 * @ticket EB-4196
 */
export interface RecordingLocation {
  url: string;
  /** `CDR_AGENT_ACCOUNTID`. */
  agentId: number;
}

/**
 * Marks the conditions `findRecordingUrl` raises itself — no agent, or a date
 * that will not compose — which are the only ones that may render the AD-6
 * empty state. Status cannot carry this: these are 404s and so is an upstream
 * miss on `accountingdata`.
 *
 * @ticket EB-4196
 */
export const RECORDING_NOT_LOCATABLE = 'recording-not-locatable';

/**
 * True when the error is one of the checks above rather than a transport
 * failure.
 *
 * @ticket EB-4196
 */
export function isRecordingNotLocatable(err: unknown): boolean {
  return (
    (err as HttpErrorResponse | null)?.error?.code === RECORDING_NOT_LOCATABLE
  );
}

/**
 * Fetches the four data sets displayed in the Call Details dialog.
 *
 * Endpoints:
 *   Accounting / CDR Data : GET /phoneservice/v3/calls/callinformation/{cchsid}/accountingdata
 *   Call Notes            : GET /document/v1/documents/{callNoteId}/children
 *   Billing               : GET /invoice/v1/billings?documentId={callNoteId}
 *   Transcript            : GET /transcription/v1/call-transcripts/{cchsid}
 *
 * Each method is stateless — results are consumed directly by the tab component.
 */
@Injectable({ providedIn: 'root' })
export class CallDetailsService {
  private readonly http = inject(HttpClient);

  findAccountingData(cchsid: string): Observable<CdrAccountingEntry[]> {
    return this.http.get<CdrAccountingEntry[]>(
      `${environment.phoneServiceUrl}/calls/callinformation/${encodeURIComponent(cchsid)}/accountingdata`,
    );
  }

  findCallNotes(callNoteId: number): Observable<CallNoteEntry[]> {
    return this.http.get<CallNoteEntry[]>(
      `${environment.documentUrl}/documents/${callNoteId}/children`,
    );
  }

  findBillings(callNoteId: number): Observable<BillingEntry[]> {
    return this.http.get<BillingEntry[]>(`${environment.invoiceUrl}/billings`, {
      params: { documentId: String(callNoteId) },
    });
  }

  findTranscript(
    cchsid: string,
    callStartTimestamp?: string | null,
  ): Observable<TranscriptEntry[]> {
    return this.http
      .get<unknown>(
        `${environment.transcriptionUrl}/call-transcripts/${encodeURIComponent(cchsid)}`,
        { params: { view: 'raw' } },
      )
      .pipe(
        map((raw) =>
          normalizeTranscriptResponse(raw).map((r, i) =>
            toTranscriptEntry(r, i, callStartTimestamp),
          ),
        ),
      );
  }

  /**
   * Resolve where this call's recording lives in TAPStore. No new API surface —
   * reuses `findAccountingData` for the agent id (AD-1).
   *
   * AD-12: date and agent come from different sources on purpose. The date is
   * the call's Started time (one value per call); the agent is any row that has
   * one (they all share it). Using a row's own time for the date would make the
   * folder depend on response ordering.
   *
   * AD-4: call this on tab activation only — the audit line means "one intent
   * to listen" only while that stays true.
   *
   * @param callStartTimestamp absent on deep links; falls back to the earliest
   *   agent-carrying event time (sorted, not indexed), the ambiguous path AD-11
   *   accepts.
   * @returns errors when no row has a usable agent or the path won't compose,
   *   both marked `RECORDING_NOT_LOCATABLE` so the caller can render them as the
   *   AD-6 empty state and everything else as an error alert.
   * @ticket EB-4196
   */
  findRecordingUrl(
    cchsid: string,
    callStartTimestamp?: string | null,
  ): Observable<RecordingLocation> {
    return this.findAccountingData(cchsid).pipe(
      map((rows) => {
        const withAgent = (rows ?? [])
          .map((row) => ({ row, agentId: toAgentId(row.agent) }))
          .filter((r): r is { row: CdrAccountingEntry; agentId: number } => r.agentId != null);
        const first = withAgent[0];
        if (!first) {
          throw new HttpErrorResponse({
            status: 404,
            statusText: 'Not Found',
            error: {
              code: RECORDING_NOT_LOCATABLE,
              reason: 'no-agent',
              message: 'No agent on this call — cannot locate a recording',
            },
          });
        }
        // Sort by instant, not lexicographically: ISO strings with differing
        // offsets order wrongly as text. Across the autumn DST fall-back the
        // same wall-clock hour repeats with +02:00 then +01:00, so
        // "02:30:00+02:00" (the earlier instant) sorts AFTER "02:15:00+01:00".
        const earliestEventTime = withAgent
          .map((r) => r.row.timestamp)
          .filter((t): t is string => !!t)
          .map((t) => ({ raw: t, ms: new Date(t).getTime() }))
          .filter((t) => Number.isFinite(t.ms))
          .sort((a, b) => a.ms - b.ms)[0]?.raw;
        const date = callStartTimestamp || earliestEventTime;
        const url = buildRecordingUrl(environment.tapStoreUrl, cchsid, date, first.agentId);
        if (!url) {
          throw new HttpErrorResponse({
            status: 404,
            statusText: 'Not Found',
            error: {
              code: RECORDING_NOT_LOCATABLE,
              reason: 'unusable-date',
              message: 'Could not compose a recording path for this call',
            },
          });
        }
        return { url, agentId: first.agentId };
      }),
    );
  }

  /**
   * Resolve the phone service id for a user account.
   * Returns the `id` of the first matching phone service, or null when the
   * endpoint responds with an empty array.
   */
  findPhoneServiceIdByUserAccount(userAccountId: number): Observable<number | null> {
    return this.http
      .get<Array<{ id: number | string }>>(`${environment.phoneServiceUrl}/phoneservices`, {
        params: { userAccountId: String(userAccountId) },
      })
      .pipe(
        map((services) => {
          // `id` can be either a number or a numeric string depending on the endpoint.
          const raw = services?.[0]?.id;
          if (raw == null) return null;
          const n = typeof raw === 'number' ? raw : Number(raw);
          return Number.isFinite(n) ? n : null;
        }),
      );
  }

  /**
   * Fetch the "Call Details" tab payload, preferring the rich phoneservice-api
   * endpoint and falling back to the document-api when the former returns 200 +
   * empty body (a quirk for TESTAUFRUF and certain unsaved drafts).
   *
   * Flow:
   *   1. Resolve phoneServiceId via `?userAccountId=...`
   *   2. GET /phoneservice/v3/phoneservices/{phoneServiceId}/callnotes/{callNoteId}
   *   3. If the body is empty OR step 1/2 errors → GET /document/v1/documents/callnotes/{callNoteId}
   *      and map the leaner shape into `CallNoteDetails`.
   *
   * Throws when callNoteId is missing — the caller surfaces that via the tab's error banner.
   * `userAccountId` is optional: when absent we skip the phoneservice path and go straight to
   * the document-api fallback. That makes deep-links (which may not carry a UA) work too.
   */
  findCallNoteDetails(
    userAccountId: number | null | undefined,
    callNoteId: number | null | undefined,
  ): Observable<CallNoteDetails> {
    if (callNoteId == null) {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 404,
            statusText: 'Not Found',
            error: 'Call Details unavailable — missing callnote ID',
          }),
      );
    }
    const fallback = (): Observable<CallNoteDetails> => this.findCallNoteFromDocumentApi(callNoteId);

    if (userAccountId == null) {
      return fallback();
    }

    return this.findPhoneServiceIdByUserAccount(userAccountId).pipe(
      switchMap((phoneServiceId) => {
        if (phoneServiceId == null) return fallback();
        return this.http
          .get<CallNoteDetails | null>(
            `${environment.phoneServiceUrl}/phoneservices/${phoneServiceId}/callnotes/${callNoteId}`,
          )
          .pipe(
            switchMap((rich) => (rich ? of(rich) : fallback())),
            catchError(() => fallback()),
          );
      }),
      catchError(() => fallback()),
    );
  }

  /**
   * Document-api fallback: leaner shape, but always present for any callnote id
   * (including TESTAUFRUF). Maps the German-named fields to the UI's
   * `CallNoteDetails` shape — caller details / folder / labels stay null since
   * the doc-api doesn't carry them. `phoneServiceName` and `customerCompany`
   * are enriched via two follow-up GETs (phoneservice description, customer
   * companyName) so the Recipient card never reads "— (PS 12345)" when the id
   * is actually known. Both enrichment calls fall back to null on error.
   */
  private findCallNoteFromDocumentApi(callNoteId: number): Observable<CallNoteDetails> {
    return this.http
      .get<CallNoteDocumentDto>(
        `${environment.documentUrl}/documents/callnotes/${callNoteId}`,
      )
      .pipe(
        map(toCallNoteDetailsFromDocApi),
        switchMap((base) => this.enrichWithPhoneServiceAndCustomer(base)),
      );
  }

  /**
   * Look up the phoneservice (for `description`) and, via its `customerId`,
   * the customer (for `companyName`). Sequential because customerId comes
   * from the PS response. Either step erroring leaves the corresponding
   * field as whatever the doc-api mapper put there (typically null).
   */
  private enrichWithPhoneServiceAndCustomer(base: CallNoteDetails): Observable<CallNoteDetails> {
    if (base.phoneServiceId == null) return of(base);
    return this.http
      .get<{ description?: string | null; customerId?: string | number | null }>(
        `${environment.phoneServiceUrl}/phoneservices/${base.phoneServiceId}`,
      )
      .pipe(
        catchError(() => of(null as { description?: string | null; customerId?: string | number | null } | null)),
        switchMap((ps) => {
          const psName = ps?.description ?? base.phoneServiceName ?? null;
          const customerId = ps?.customerId ?? null;
          if (customerId == null) {
            return of({ ...base, phoneServiceName: psName });
          }
          return this.http
            .get<{ companyName?: string | null }>(
              `${environment.contractUrl}/customers/${customerId}`,
            )
            .pipe(
              catchError(() => of(null as { companyName?: string | null } | null)),
              map((cust) => ({
                ...base,
                phoneServiceName: psName,
                customerCompany: cust?.companyName ?? base.customerCompany ?? null,
              })),
            );
        }),
      );
  }
}

/**
 * Map the document-api callnote DTO into the rich `CallNoteDetails` shape used
 * by the UI. The doc-api response is flatter and German-named — we fill the
 * fields we can (subject → shortContent, notiz → content, vorwahl/prefix/suffix
 * → callee, anrufer* → caller, formfieldValues → formfields) and leave the
 * fields the doc-api doesn't carry as null. The Call Details tab renders the
 * null sections as em-dashes / hides them gracefully.
 */
function toCallNoteDetailsFromDocApi(dto: CallNoteDocumentDto): CallNoteDetails {
  const callerName = [dto.anruferVorname, dto.anruferNachname].filter((s): s is string => !!s);
  const hasCaller = !!(
    dto.anruferVorname || dto.anruferNachname || dto.anruferFirma || dto.anruferTelefon
  );
  return {
    id: String(dto.id),
    from: null,
    to: null,
    shortContent: dto.subject ?? dto.gespraechSubject ?? null,
    content: dto.notiz ?? null,
    userAccountId: dto.userAccountId ?? null,
    phoneServiceId: dto.phoneServiceId ?? null,
    type: dto.type ?? null,
    color: null,
    created: dto.created ?? dto.bearbeitungStart ?? null,
    lastChange: dto.bearbeitungEnde ?? null,
    priority: dto.priority ?? null,
    status: dto.status ?? null,
    read: dto.status !== 'UNGELESEN',
    urgent: !!dto.urgent,
    inTrash: false,
    deleted: false,
    phoneServiceName: null,
    customerCompany: null,
    labels: null,
    outboundTasks: null,
    outboundTaskAttempts: null,
    callNoteType: dto.callNoteStatus ?? null,
    caller: hasCaller
      ? {
          salutation: dto.anruferAnrede ?? null,
          firstname: dto.anruferVorname ?? null,
          lastname: dto.anruferNachname ?? null,
          company: dto.anruferFirma ?? null,
          vip: !!dto.vip,
          email: null,
          phone: dto.anruferTelefon ?? null,
        }
      : null,
    callee: dto.vorwahl || dto.prefix || dto.suffix
      ? {
          vorwahl: dto.vorwahl ?? null,
          prefix: dto.prefix ?? null,
          suffix: dto.suffix ?? null,
          realVorwahl: dto.realVorwahl ?? null,
          realPrefix: dto.realPrefix ?? null,
          realSuffix: dto.realSuffix ?? null,
        }
      : null,
    contactPerson: dto.ansprechPartner ?? null,
    clipTelephone: dto.clipTelefon ?? null,
    callReason: null,
    callcenterAgentSignature: null,
    agentName: dto.handledAgentName ?? null,
    agentId: dto.mitarbeiter ?? null,
    addressbookPersonId: null,
    calendarEvents: null,
    feedback: null,
    aiCallData: null,
    attachments: dto.attachments ?? null,
    folder: null,
    formfields: (dto.formfieldValues ?? []).map((f) => ({
      name: f.name,
      type: String(f.type),
      value: (f.inputValue ?? f.value ?? null) || null,
      valid: !!f.valid,
      sort: f.sort,
      abDetailType: (f.abDetailTypeName ?? (f.abDetailType != null ? String(f.abDetailType) : null)),
      id: String(f.id),
    })),
    // Caller name (composed) helps the Caller card surface "Vorname Nachname" cleanly.
    // The model carries firstname/lastname separately so the existing template already
    // composes from those — no extra field needed.
    ...(callerName.length > 0 ? {} : {}),
  };
}

/**
 * Normalize the v1 call-transcript response into a flat list of entry DTOs.
 * Flattens `conversations[].transcript.views.raw.entries[]` across ALL
 * conversations (an operator wants the whole call, transfer legs included).
 * Returns [] for null / non-object / missing / empty payloads, and skips
 * null / primitive / array ELEMENTS inside `conversations` / `entries`, so
 * the empty-state branch renders cleanly (the API returns 200 even when empty).
 */
function normalizeTranscriptResponse(raw: unknown): TranscriptEntryDto[] {
  if (raw == null || typeof raw !== 'object') return [];
  const obj = raw as Record<string, unknown>;
  if (!Array.isArray(obj['conversations'])) return [];
  const all: TranscriptEntryDto[] = [];
  for (const rawConv of obj['conversations'] as unknown[]) {
    if (rawConv == null || typeof rawConv !== 'object' || Array.isArray(rawConv)) continue;
    const conv = rawConv as Record<string, unknown>;
    const transcript = conv['transcript'] as Record<string, unknown> | undefined;
    const views = transcript?.['views'] as Record<string, unknown> | undefined;
    const rawView = views?.['raw'] as Record<string, unknown> | undefined;
    const entries = rawView?.['entries'];
    if (Array.isArray(entries)) {
      all.push(
        ...(entries as unknown[]).filter(
          (e): e is TranscriptEntryDto =>
            e != null && typeof e === 'object' && !Array.isArray(e),
        ),
      );
    }
  }
  return all;
}

/**
 * Map a v1 transcript entry to the UI model. `speaker` is UPPERCASED at this
 * boundary (v1 is lowercase; the tab template compares `speaker === 'AGENT'`),
 * `content` → `text`, and `timestamp = callStart + startMs` (ms) when the call
 * start is known and the sum is a representable date. v1 carries no per-line
 * id or display name, so `id` = the
 * flattened array index and `speakerName` = null (`actorDisplayName()` falls
 * back to the call summary / role label).
 */
function toTranscriptEntry(
  dto: TranscriptEntryDto,
  index: number,
  callStartTimestamp?: string | null,
): TranscriptEntry {
  let timestamp: string | null | undefined;
  if (callStartTimestamp && typeof dto.startMs === 'number') {
    const callStartMs = new Date(callStartTimestamp).getTime();
    if (Number.isFinite(callStartMs)) {
      // Hostile startMs (Infinity from JSON `1e309`, or beyond the ±8.64e15 Date
      // range) yields an Invalid Date whose toISOString() throws — skip instead.
      const lineDate = new Date(callStartMs + dto.startMs);
      if (!Number.isNaN(lineDate.getTime())) timestamp = lineDate.toISOString();
    }
  }
  return {
    id: index,
    speaker: String(dto.speaker ?? '').toUpperCase(),
    text: String(dto.content ?? ''),
    timestamp,
    speakerName: null,
  };
}
